/**
 * Money received past a full Mexican issuer: what a manager reads and decides.
 *
 * A held deposit is credited to the client and waits uninvested in the receiving fund. After
 * the owner's days a decision is raised: offer the return (the client is told and their Account
 * Executive follows up; the client withdraws through the ordinary flow) or keep waiting, with a
 * reason, and it is raised again after the same days.
 */
import type { HeldDeposit, HoldEvent, HoldState } from '../../services/heldDeposits.service';

/** The shortest reason that can be recorded for keeping the money waiting. */
export const MIN_REASON = 10;

export const STATE_LABEL: Record<HoldState, string> = {
  waiting: 'Waiting',
  decision_due: 'Decision due',
  return_offered: 'Return offered',
  ended: 'No longer held',
};

export const EVENT_LABEL: Record<HoldEvent['kind'], string> = {
  decision_due: 'Decision raised',
  return_offered: 'Return offered',
  keep_waiting: 'Kept waiting',
  hold_ended: 'Hold ended',
};

const END_REASON: Record<string, string> = {
  slot_freed: 'the fund has room for the person again',
  money_withdrawn: 'the money was withdrawn',
};

/** The reason of a history entry, in words: the manager's own for a decision to keep waiting,
 *  and the fact that ended the hold. Null when the entry carries none. */
export function eventReason(event: HoldEvent): string | null {
  if (event.kind === 'hold_ended') return END_REASON[event.reason ?? ''] ?? null;
  return event.reason;
}

/** How long the money has been held. */
export function heldFor(days: number): string {
  return days === 1 ? 'Held for 1 day' : `Held for ${days} days`;
}

/** When the next decision is raised, for money that is waiting. */
export function nextDecisionLine(day: string): string {
  return `Next decision on ${day}`;
}

/** How many decisions wait for the reader. */
export function decisionsWaiting(count: number): string {
  return count === 1 ? '1 decision is waiting for you.' : `${count} decisions are waiting for you.`;
}

/** Whether the manager can decide now: only while a decision is raised. */
export function mayDecide(hold: Pick<HeldDeposit, 'state'>): boolean {
  return hold.state === 'decision_due';
}

/** Why the reason cannot be recorded yet, or null when it can. */
export function reasonProblem(reason: string): string | null {
  const cleaned = reason.trim().replace(/\s+/g, ' ');
  if (cleaned.length < MIN_REASON) {
    return `Write why the money keeps waiting (at least ${MIN_REASON} characters).`;
  }
  return null;
}

/** The held deposits grouped by the fund that holds them, oldest first inside each. */
export function byFund(holds: HeldDeposit[]): Array<[string, HeldDeposit[]]> {
  const groups = new Map<string, HeldDeposit[]>();
  for (const hold of holds) {
    groups.set(hold.legal_entity_id, [...(groups.get(hold.legal_entity_id) ?? []), hold]);
  }
  return [...groups.entries()];
}
